import { cloneElement, isValidElement, useCallback, useMemo, type ReactElement } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../../../../ui/input";
import { Field } from "../../../Form";
import Button from "../../../Button";
import Select from "../../../inputs/Select";
import { findFilterConfig, getDefaultFilterValue, getFieldMethod, type FieldType } from "../helpers";
import type { FilterConfig, FiltersFormValues, ItemProps } from "../types";

function Item({ item, index, insert, remove, config }: ItemProps) {
    const { t } = useTranslation();
    const { register, control, setValue } = useFormContext<FiltersFormValues>();
    const list = useWatch({ control, name: "filters" });
    const current = useMemo(() => list?.[index], [index, list]);

    const options = useMemo(() => {
        const isTaken = (name: string) =>
            list?.findIndex((row) => row?.name === name) !== -1 && current?.name !== name;

        return config.reduce<FilterConfig[]>((acc, entry) => {
            if (entry.children) {
                const children = entry.children.filter((child) => !isTaken(child.name));
                if (children.length > 0) acc.push({ ...entry, children });
            } else if (!isTaken(entry.name)) {
                acc.push(entry);
            }
            return acc;
        }, []);
    }, [config, list, current]);

    const itemConfig = useMemo(() => findFilterConfig(config, current?.name), [config, current?.name]);
    
    const onNameChange = useCallback((name?: string) => {
        const fieldType = findFilterConfig(config, name)?.type as FieldType | undefined;
        setValue(`filters.${index}.value`, getDefaultFilterValue(fieldType));
        setValue(`filters.${index}.method`, getFieldMethod(fieldType));
    }, [config, index, setValue]);

    const valueControl = useMemo(() => {
        const { component, disabled } = itemConfig ?? {};

        if (isValidElement(component)) {
            if ((component.props as { disabled?: boolean }).disabled !== undefined) {
                return component;
            }
            return cloneElement(component as ReactElement<any>, {
                disabled,
                className: "tw-w-full tw-min-w-0",
            });
        }

        return <Input placeholder={t("value")} disabled={disabled} />;
    }, [itemConfig, t]);

    return (
        <div key={item.id} className="tw-flex tw-items-end tw-gap-2">
            <Field
                name={`filters.${index}.name`}
                className="tw-mb-0 tw-flex-1"
            >
                <Select onChange={onNameChange}>
                    {options.map((option) =>
                        option.children ? (
                            <Select.Group key={option.name} heading={option.label || t(option.name)}>
                                {option.children.map((child) => (
                                    <Select.Item key={child.name} value={child.name}>
                                        {child.label || t(child.name)}
                                    </Select.Item>
                                ))}
                            </Select.Group>
                        ) : (
                            <Select.Item key={option.name} value={option.name}>
                                {option?.label || t(option.name)}
                            </Select.Item>
                        )
                    )}
                </Select>
            </Field>

            <input type="hidden" {...register(`filters.${index}.method`)} />

            <Field
                key={itemConfig?.type ?? "text"}
                name={`filters.${index}.value`}
                className="tw-mb-0 tw-flex-1"
            >
                {valueControl}
            </Field>

            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-border tw-border-dashed"
                onClick={() => insert(index + 1, { name: "", method: "", value: "" })}
            >
                <Plus className="tw-size-4" />
            </Button>

            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-bg-rose-50 tw-text-rose-500"
                disabled={list?.length === 1}
                onClick={() => remove(index)}
            >
                <Trash2 className="tw-size-4" />
            </Button>
        </div>
    );
}

export default Item;
